import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Leave Balances Dashboard
 * Shows holiday entitlement overview for all employees
 * 
 * API Endpoints used:
 * - GET /api/leave-balances/summary - Get all employee balances
 * - GET /api/leave-balances/export - Download CSV
 * - GET /api/leave-balances/employee/:id - Individual employee detail
 */

const LeaveBalances = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchBalances();
  }, []);

  const fetchBalances = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/leave-balances/summary', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setData(response.data);
      } else {
        setError('Failed to load leave balances');
      }
    } catch (err) {
      console.error('Error fetching leave balances:', err);
      setError(err.response?.data?.error || 'Failed to load leave balances');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/leave-balances/export', {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Leave_Balances_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Failed to export leave balances');
    } finally {
      setExporting(false);
    }
  };

  const handleViewEmployee = async (employee) => {
    try {
      setSelectedEmployee(employee);
      setShowDetailModal(true);
      
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/leave-balances/employee/${employee.employee_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setEmployeeDetail(response.data);
      }
    } catch (err) {
      console.error('Error fetching employee detail:', err);
    }
  };

  const closeModal = () => {
    setShowDetailModal(false);
    setSelectedEmployee(null);
    setEmployeeDetail(null);
  };

  // Progress bar component
  const ProgressBar = ({ taken, total, pending = 0 }) => {
    const takenPercent = Math.min((taken / total) * 100, 100);
    const pendingPercent = Math.min((pending / total) * 100, 100 - takenPercent);
    
    return (
      <div style={styles.progressContainer}>
        <div style={styles.progressBar}>
          <div 
            style={{
              ...styles.progressFill,
              width: `${takenPercent}%`,
              backgroundColor: takenPercent > 90 ? '#10b981' : takenPercent > 50 ? '#3b82f6' : '#f59e0b'
            }}
          />
          {pending > 0 && (
            <div 
              style={{
                ...styles.progressFill,
                width: `${pendingPercent}%`,
                backgroundColor: '#94a3b8',
                marginLeft: `${takenPercent}%`,
                position: 'absolute'
              }}
            />
          )}
        </div>
        <span style={styles.progressText}>{taken}/{total}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading leave balances...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <p>{error}</p>
          <button onClick={fetchBalances} style={styles.retryButton}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Leave Balances</h1>
          <p style={styles.subtitle}>
            Holiday Year: {data?.holiday_year?.display}
          </p>
        </div>
        <button 
          onClick={handleExport} 
          disabled={exporting}
          style={styles.exportButton}
        >
          {exporting ? 'Exporting...' : '📥 Export CSV'}
        </button>
      </div>

      {/* Summary Cards */}
      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Total Employees</div>
          <div style={styles.summaryValue}>{data?.summary?.total_employees}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Total Entitlement</div>
          <div style={styles.summaryValue}>{data?.summary?.total_entitlement} days</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Days Taken</div>
          <div style={styles.summaryValue}>{data?.summary?.total_taken} days</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Days Remaining</div>
          <div style={{...styles.summaryValue, color: '#f59e0b'}}>
            {data?.summary?.total_remaining} days
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Pending Approval</div>
          <div style={{...styles.summaryValue, color: '#6366f1'}}>
            {data?.summary?.total_pending} days
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Overall Utilisation</div>
          <div style={styles.summaryValue}>{data?.summary?.overall_utilisation}%</div>
        </div>
      </div>

      {/* Employee Table */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Employee</th>
              <th style={styles.th}>Role</th>
              <th style={styles.thCenter}>Entitlement</th>
              <th style={styles.thCenter}>Taken</th>
              <th style={styles.thCenter}>Pending</th>
              <th style={styles.thCenter}>Remaining</th>
              <th style={styles.th}>Progress</th>
              <th style={styles.thCenter}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.employees?.map((emp) => (
              <tr 
                key={emp.employee_id} 
                style={{
                  ...styles.tr,
                  backgroundColor: emp.at_risk ? '#fef3c7' : 'transparent'
                }}
              >
                <td style={styles.td}>
                  <div style={styles.employeeName}>{emp.employee_name}</div>
                  {emp.at_risk && (
                    <span style={styles.warningBadge}>⚠️ Low utilisation</span>
                  )}
                </td>
                <td style={styles.td}>{emp.job_role}</td>
                <td style={styles.tdCenter}>{emp.total_entitlement}</td>
                <td style={styles.tdCenter}>
                  <span style={styles.takenBadge}>{emp.days_taken}</span>
                </td>
                <td style={styles.tdCenter}>
                  {emp.days_pending > 0 ? (
                    <span style={styles.pendingBadge}>{emp.days_pending}</span>
                  ) : '-'}
                </td>
                <td style={styles.tdCenter}>
                  <span style={{
                    ...styles.remainingBadge,
                    backgroundColor: emp.days_remaining <= 5 ? '#dcfce7' : 
                                    emp.days_remaining >= 20 ? '#fef3c7' : '#e0e7ff'
                  }}>
                    {emp.days_remaining}
                  </span>
                </td>
                <td style={styles.td}>
                  <ProgressBar 
                    taken={emp.days_taken} 
                    total={emp.total_entitlement}
                    pending={emp.days_pending}
                  />
                </td>
                <td style={styles.tdCenter}>
                  <button 
                    onClick={() => handleViewEmployee(emp)}
                    style={styles.viewButton}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <span style={{...styles.legendDot, backgroundColor: '#f59e0b'}}></span>
          <span>&lt;50% used</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{...styles.legendDot, backgroundColor: '#3b82f6'}}></span>
          <span>50-90% used</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{...styles.legendDot, backgroundColor: '#10b981'}}></span>
          <span>&gt;90% used</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{...styles.legendDot, backgroundColor: '#94a3b8'}}></span>
          <span>Pending</span>
        </div>
      </div>

      {/* Employee Detail Modal */}
      {showDetailModal && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {selectedEmployee?.employee_name}
              </h2>
              <button onClick={closeModal} style={styles.closeButton}>×</button>
            </div>
            
            {employeeDetail ? (
              <div style={styles.modalContent}>
                <p style={styles.modalSubtitle}>{employeeDetail.employee?.job_role}</p>
                <p style={styles.holidayYear}>{employeeDetail.holiday_year?.display}</p>
                
                {/* Balance Summary */}
                <div style={styles.balanceGrid}>
                  <div style={styles.balanceItem}>
                    <div style={styles.balanceLabel}>Entitlement</div>
                    <div style={styles.balanceValue}>{employeeDetail.balance?.total_entitlement}</div>
                  </div>
                  <div style={styles.balanceItem}>
                    <div style={styles.balanceLabel}>Taken</div>
                    <div style={styles.balanceValue}>{employeeDetail.balance?.days_taken}</div>
                  </div>
                  <div style={styles.balanceItem}>
                    <div style={styles.balanceLabel}>Pending</div>
                    <div style={styles.balanceValue}>{employeeDetail.balance?.days_pending}</div>
                  </div>
                  <div style={styles.balanceItem}>
                    <div style={styles.balanceLabel}>Remaining</div>
                    <div style={{...styles.balanceValue, color: '#10b981', fontWeight: '700'}}>
                      {employeeDetail.balance?.days_remaining}
                    </div>
                  </div>
                </div>

                {/* Approved Requests */}
                {employeeDetail.approved_requests?.length > 0 && (
                  <div style={styles.requestSection}>
                    <h3 style={styles.requestTitle}>Approved Leave</h3>
                    {employeeDetail.approved_requests.map(req => (
                      <div key={req.id} style={styles.requestItem}>
                        <span>{formatDate(req.start_date)} - {formatDate(req.end_date)}</span>
                        <span style={styles.requestDays}>{req.days} days</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pending Requests */}
                {employeeDetail.pending_requests?.length > 0 && (
                  <div style={styles.requestSection}>
                    <h3 style={{...styles.requestTitle, color: '#6366f1'}}>Pending Approval</h3>
                    {employeeDetail.pending_requests.map(req => (
                      <div key={req.id} style={{...styles.requestItem, backgroundColor: '#eef2ff'}}>
                        <span>{formatDate(req.start_date)} - {formatDate(req.end_date)}</span>
                        <span style={styles.requestDays}>{req.days} days</span>
                      </div>
                    ))}
                  </div>
                )}

                {employeeDetail.approved_requests?.length === 0 && 
                 employeeDetail.pending_requests?.length === 0 && (
                  <p style={styles.noRequests}>No leave requests this holiday year</p>
                )}
              </div>
            ) : (
              <div style={styles.modalLoading}>Loading...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'short',
    year: 'numeric'
  });
};

// Styles
const styles = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  exportButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
    marginBottom: '24px'
  },
  summaryCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  summaryLabel: {
    fontSize: '13px',
    color: '#64748b',
    marginBottom: '8px',
    fontWeight: '500'
  },
  summaryValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b'
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    marginBottom: '16px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    padding: '14px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc'
  },
  thCenter: {
    padding: '14px 16px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0',
    transition: 'background-color 0.15s'
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#334155'
  },
  tdCenter: {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#334155',
    textAlign: 'center'
  },
  employeeName: {
    fontWeight: '600',
    color: '#1e293b'
  },
  warningBadge: {
    fontSize: '11px',
    color: '#b45309',
    display: 'block',
    marginTop: '2px'
  },
  takenBadge: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600'
  },
  pendingBadge: {
    backgroundColor: '#e0e7ff',
    color: '#4338ca',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600'
  },
  remainingBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#334155'
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  progressBar: {
    flex: 1,
    height: '8px',
    backgroundColor: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden',
    position: 'relative',
    minWidth: '100px'
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease'
  },
  progressText: {
    fontSize: '12px',
    color: '#64748b',
    minWidth: '45px',
    textAlign: 'right'
  },
  viewButton: {
    padding: '6px 14px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  legend: {
    display: 'flex',
    gap: '20px',
    justifyContent: 'center',
    padding: '12px',
    fontSize: '13px',
    color: '#64748b'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  legendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '3px'
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    color: '#64748b',
    fontSize: '16px'
  },
  error: {
    textAlign: 'center',
    padding: '60px',
    color: '#dc2626'
  },
  retryButton: {
    marginTop: '16px',
    padding: '10px 24px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer'
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0'
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '0',
    lineHeight: '1'
  },
  modalContent: {
    padding: '24px'
  },
  modalSubtitle: {
    color: '#64748b',
    margin: '0 0 4px 0',
    fontSize: '14px'
  },
  holidayYear: {
    fontSize: '13px',
    color: '#94a3b8',
    margin: '0 0 20px 0'
  },
  balanceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginBottom: '24px'
  },
  balanceItem: {
    textAlign: 'center',
    padding: '16px 8px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px'
  },
  balanceLabel: {
    fontSize: '11px',
    color: '#64748b',
    marginBottom: '6px',
    fontWeight: '500',
    textTransform: 'uppercase'
  },
  balanceValue: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1e293b'
  },
  requestSection: {
    marginTop: '20px'
  },
  requestTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#334155',
    marginBottom: '12px'
  },
  requestItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    marginBottom: '8px',
    fontSize: '14px'
  },
  requestDays: {
    fontWeight: '600',
    color: '#3b82f6'
  },
  noRequests: {
    textAlign: 'center',
    color: '#94a3b8',
    padding: '30px',
    fontSize: '14px'
  },
  modalLoading: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b'
  }
};

export default LeaveBalances;